import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { NAVIGATE_PATH as PATH } from '../../../helpers/constants';
import { resolveApiMessage, showToast } from '../../../helpers';
import {
  addMyLearner,
  cancelMyRegistration,
  getMyEvent,
  getMyEvents,
  TAddPerson,
} from '../../../requests/customerPortal';
import {
  btnGhost,
  btnSecondary,
  Card,
  EmptyState,
  inputClass,
  PageBody,
  PageHeader,
  Pill,
  rowClickClass,
  tableClass,
  tdClass,
  theadClass,
  thClass,
  Toolbar,
} from '../../Admin/ui';
import {
  AddPersonForm,
  Field,
  fmtDateTime,
  fmtMoney,
  fmtNum,
  LoadFailed,
  retryUnless4xx,
} from '../shared';

const ExtLink: React.FC<{ href?: string | null; label: string }> = ({
  href,
  label,
}) =>
  href ? (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="text-sm font-medium text-blue-primary hover:underline"
    >
      {label} ↗
    </a>
  ) : (
    <span className="text-sm text-gray-two">{label}: not set</span>
  );

const EventDetail: React.FC<{ id: string }> = ({ id }) => {
  const [showAll, setShowAll] = useState(false);
  const [adding, setAdding] = useState(false);
  const qc = useQueryClient();
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['my', 'event', id] });
    qc.invalidateQueries({ queryKey: ['my', 'events'] });
    qc.invalidateQueries({ queryKey: ['my', 'registrations'] });
  };
  const add = useMutation({
    mutationFn: (body: TAddPerson) => addMyLearner(id, body),
    onSuccess: (r) => {
      showToast(
        r.status === 'Waiting List'
          ? 'Added to the waiting list'
          : `Seat booked · ${r.booked ?? '—'} of ${r.seats ?? '—'} seats taken`,
        'success',
      );
      setAdding(false);
      refresh();
    },
    onError: (err) => showToast(resolveApiMessage(err), 'error'),
  });
  const cancel = useMutation({
    mutationFn: (regId: string) => cancelMyRegistration(regId),
    onSuccess: () => {
      showToast('Seat released', 'success');
      refresh();
    },
    onError: (err) => showToast(resolveApiMessage(err), 'error'),
  });
  const {
    data,
    isPending: isLoading,
    error,
  } = useQuery({
    retry: retryUnless4xx,
    queryKey: ['my', 'event', id],
    queryFn: () => getMyEvent(id),
  });
  if (isLoading) return <LoadingSpinner />;
  if (error || !data) return <LoadFailed error={error} />;
  const regs = showAll ? data.registrations : data.registrations.slice(0, 8);
  const open = !['Cancelled', 'Completed'].includes(data.status ?? '');
  return (
    <>
      <PageHeader
        title={data.name ?? 'Private event'}
        meta={
          <>
            <Pill value={data.status} /> · {fmtDateTime(data.start)}
          </>
        }
        back={{ to: PATH.adminEvents, label: 'All private events' }}
      />
      <PageBody>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-xl border border-white-regular bg-white px-5 py-3">
          <ExtLink href={data.privateClassLink} label="Private class link" />
          <ExtLink href={data.materialsLink} label="Course materials" />
          <ExtLink href={data.surveyLink} label="Survey link" />
          {open && (
            <Link
              to={`${PATH.adminRequestCourse}?change=${id}`}
              className={`${btnSecondary} ml-auto`}
            >
              Ask for a date or time change
            </Link>
          )}
        </div>
        <Card title="Class information" className="mt-6">
          <div className="grid gap-x-8 gap-y-4 px-5 py-4 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Starts">{fmtDateTime(data.start)}</Field>
            <Field label="Ends">{fmtDateTime(data.end)}</Field>
            <Field label="Duration">
              {data.durationHours != null
                ? `${fmtNum(data.durationHours, 1)} hours`
                : '—'}
            </Field>
            <Field label="Instructor">
              {data.instructor ?? 'To be assigned'}
            </Field>
            <Field label="Student location">{data.location ?? '—'}</Field>
            <Field label="Video conferencing">
              {data.videoProvider ?? '—'}
            </Field>
            <Field label="Seats allowed">{fmtNum(data.seatsAllowed)}</Field>
            <Field label="Seats booked">{fmtNum(data.seatsBooked)}</Field>
            <Field label="Attended">
              {data.attended != null
                ? fmtNum(data.attended)
                : 'After the class'}
            </Field>
            {data.cost != null && (
              <Field label="Course cost">{fmtMoney(data.cost)}</Field>
            )}
          </div>
        </Card>
        <Card
          title={`Registrations · ${data.registrations.length}`}
          className="mt-6"
          right={
            <div className="flex gap-2">
              {data.registrations.length > 8 && (
                <button
                  className={btnGhost}
                  onClick={() => setShowAll((v) => !v)}
                >
                  {showAll ? 'Show fewer' : 'View all'}
                </button>
              )}
              {open && !adding && (
                <button
                  className={btnSecondary}
                  onClick={() => setAdding(true)}
                >
                  Add a learner
                </button>
              )}
            </div>
          }
        >
          {adding && (
            <div className="px-5 pt-4">
              <AddPersonForm
                label="Add one of your people to this event"
                busy={add.isPending}
                allowWaitlist
                onSubmit={(body) => add.mutate(body)}
                onCancel={() => setAdding(false)}
              />
            </div>
          )}
          {data.registrations.length === 0 ? (
            <EmptyState
              title="Nobody registered yet"
              hint="Add your people here, or share the private class link above."
            />
          ) : (
            <table className={tableClass}>
              <thead className={theadClass}>
                <tr>
                  <th className={thClass}>Registration</th>
                  <th className={thClass}>Status</th>
                  <th className={thClass}>Name</th>
                  <th className={thClass}>Email</th>
                  <th className={thClass} />
                </tr>
              </thead>
              <tbody>
                {regs.map((r) => (
                  <tr key={r.id}>
                    <td
                      className={`${tdClass} whitespace-nowrap text-gray-bold`}
                    >
                      {r.number}
                    </td>
                    <td className={tdClass}>
                      <Pill value={r.status} />
                    </td>
                    <td className={tdClass}>{r.name ?? '—'}</td>
                    <td className={tdClass}>{r.email ?? '—'}</td>
                    <td className={`${tdClass} text-right`}>
                      {open &&
                        ['Booked', 'Waiting List'].includes(r.status ?? '') && (
                          <button
                            className={btnGhost}
                            disabled={cancel.isPending}
                            onClick={() => cancel.mutate(r.id)}
                          >
                            Release seat
                          </button>
                        )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </PageBody>
    </>
  );
};

const AdminEvents: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const id = params.get('id');
  const [when, setWhen] = useState<'upcoming' | 'past' | 'all'>('upcoming');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  const {
    data,
    isPending: isLoading,
    error,
  } = useQuery({
    retry: retryUnless4xx,
    queryKey: ['my', 'events', when, q],
    queryFn: () => getMyEvents(when, q || undefined),
    enabled: !id,
  });

  if (id) return <EventDetail id={id} />;

  return (
    <>
      <PageHeader
        title="Private events"
        meta="Your scheduled private workshops. Open any event to see the details and who is registered."
      />
      <PageBody>
        <Toolbar>
          <input
            className={inputClass}
            placeholder="Search this list"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="flex gap-1">
            {(['upcoming', 'past', 'all'] as const).map((w) => (
              <button
                key={w}
                className={
                  when === w
                    ? 'rounded-lg bg-blue-100 px-3 py-2 text-sm font-semibold text-blue-primary'
                    : btnSecondary
                }
                onClick={() => setWhen(w)}
              >
                {w === 'upcoming' ? 'Upcoming' : w === 'past' ? 'Past' : 'All'}
              </button>
            ))}
          </div>
        </Toolbar>
        <Card>
          {isLoading ? (
            <LoadingSpinner />
          ) : error || !data ? (
            <LoadFailed error={error} />
          ) : data.items.length === 0 ? (
            <EmptyState
              title={
                when === 'upcoming'
                  ? 'Nothing scheduled yet'
                  : 'No events found'
              }
              hint="Use “Request a private course” to schedule one."
            />
          ) : (
            <table className={tableClass}>
              <thead className={theadClass}>
                <tr>
                  <th className={thClass}>Event</th>
                  <th className={thClass}>Course status</th>
                  <th className={thClass}>Course start</th>
                  <th className={thClass}>Instructor</th>
                  <th className={`${thClass} text-right`}>Booked</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((e) => (
                  <tr
                    key={e.id}
                    className={rowClickClass}
                    onClick={() => setParams({ id: e.id })}
                  >
                    <td className={tdClass}>
                      <div className="font-medium text-gray-five">{e.name}</div>
                      <div className="text-xs text-gray-bold">{e.location}</div>
                    </td>
                    <td className={tdClass}>
                      <Pill value={e.status} />
                    </td>
                    <td className={`${tdClass} whitespace-nowrap`}>
                      {fmtDateTime(e.start)}
                    </td>
                    <td className={tdClass}>{e.instructor ?? '—'}</td>
                    <td className={`${tdClass} text-right`}>
                      {fmtNum(e.booked)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </PageBody>
    </>
  );
};

export default AdminEvents;
